import { observer } from 'mobx-react';
import {
  FUND_SEARCH_CATEGORY_FIELDS,
  PORTFOLIO_SEARCH_CATEGORY_FIELDS,
  PROFILER_INDUSTRY_CODES_SEARCH_FIELDS,
  PROFILER_SEARCH_CATEGORY_FIELDS,
  PROFILER_SEARCH_FIELDS,
  SEARCH_FIELDS,
  SEARCH_FIELDS_PORTFOLIO_INVESTMENT_CRITERIA
} from '../../../../utils/constants/fields';
import { Fragment, useCallback, useEffect, useMemo } from 'react';
import useHistory from '../../../../hooks/useHistory';
import { useStore } from '../../../../store/store';
import { UI_OPTIONS } from '../../../../utils/constants/uiOptions';
import { UI_OPTION_KEYS } from '../../../../utils/constants/uiOptionKeys';
import MultiSelect from '../../../../components/inputs/MultiSelect';
import ProfilerCompanyNameSearch from '../../../Home/components/agent/ProfilerCompanyNameSearch';
import IndividualsNameSearch from '../../../Home/components/profiler/individuals-name/IndividualsNameSearch';
import IconButton, {
  ICON_BUTTON_ICONS,
  ICON_BUTTON_TYPES
} from '../../../../components/buttons/IconButton';
import BulkAdjustPanel from './BulkAdjustPanel';
import {
  PROFILER_INDUSTRY_CODES_SEARCH_CATEGORIES,
  PROFILER_SEARCH_CATEGORIES
} from '../../../../utils/constants/searchCategories';
import {
  isProfilerAdvancedCriteriaDisabled,
  isProfilerAdvancedCriteriaSituationDisabled,
  isProfilerAdvancedCriteriaOwnershipDisabled
} from '../../../../utils/constants/variables';

const MobileFilters = observer(
  ({
    state,
    bulkAdjust,
    fundsInvestmentCriteriaFields,
    portfoliosInvestmentCriteriaFields,
    profilerCompanyCriteriaFields,
    profilerIndustryCodesFields
  }) => {
    const { utilsStore, profilerAccessStore } = useStore();
    const { navigate, location } = useHistory();

    useEffect(() => {
      const closeFilters = () => {
        state.toggleMobileFilters();
      };
      navigate(location.pathname + location.search);
      addEventListener('popstate', closeFilters);
      return () => {
        removeEventListener('popstate', closeFilters);
        if (state.profilerIndustriesSearchTreeToggled) {
          state.toggleProfilerIndustriesSearchTree();
        }
      };
    }, [state]);

    useEffect(() => {
      if (utilsStore.windowWidth >= 834) {
        navigate(-1);
      }
    }, [utilsStore.windowWidth]);

    const fundsInvestmentCriteriafieldsSections = Object.entries(fundsInvestmentCriteriaFields)
      .filter(([id]) => id !== SEARCH_FIELDS.SEARCH_TYPE.ID)
      .reduce((arr, curr) => {
        arr.push(...Object.values(curr[1]));
        return arr;
      }, []);

    const isLoading = state.isLoading || state.initialLoading;
    const searchType = state.fundsInvestmentCriteriaFields[SEARCH_FIELDS.SEARCH_TYPE.NAME][0];

    const selectedIndustryCodes = state.industryCodesFlat.filter((code) => code.selected);

    const highlightMatches = useCallback((name, searchKey) => {
      if (!searchKey.trim()) return name;

      const lowerName = name.toLowerCase();
      const lowerSearch = searchKey.toLowerCase();
      const result = [];
      let lastIndex = 0;
      let index = lowerName.indexOf(lowerSearch);

      while (index !== -1) {
        if (index > lastIndex) result.push(name.substring(lastIndex, index));

        result.push(
          <span key={index} className="highlight">
            {name.substring(index, index + searchKey.length)}
          </span>
        );

        lastIndex = index + searchKey.length;
        index = lowerName.indexOf(lowerSearch, lastIndex);
      }

      if (lastIndex < name.length) result.push(name.substring(lastIndex));

      return result;
    }, []);

    const hasSelectedChildren = useMemo(() => {
      const lookup = new Map(state.industryCodesFlat.map((c) => [c.value, c]));
      const check = (node) => {
        const current = lookup.get(node.value);
        if (!current) return false;

        const direct = current.children?.some((child) => lookup.get(child.value)?.selected);
        if (direct) return true;

        return current.children?.some((child) => check(child)) || false;
      };
      return check;
    }, [state.industryCodesFlat]);

    const renderIndustryCode = useCallback(
      (code, level) => {
        const fromFlat = state.industryCodesFlat.find((_c) => _c.value === code.value);
        if (!fromFlat) return null;

        const hasParent = !!fromFlat.parent;
        const hasChildren = !!code.children?.length;
        const isToggled = !!fromFlat.toggled;
        const isSelected = !!fromFlat.selected;
        const showInnerSelectionsDot =
          !isToggled && !!fromFlat.children?.length && hasSelectedChildren(fromFlat);

        const handleClick = () => {
          if (hasChildren) {
            state.toggleIndustryCode(code);
          } else {
            state.selectIndustryCode(code);
          }
        };

        const handleCheckClick = (event) => {
          event.preventDefault();
          event.stopPropagation();
          event.nativeEvent?.preventDefault();
          state.selectIndustryCode(code);
        };

        return (
          <div
            className="industry-code-item-wrap"
            key={code.value}
            style={hasParent ? { marginLeft: '22px' } : {}}
          >
            <div
              className={`industry-code-item noselect ${isSelected ? 'selected' : ''}`}
              onClick={handleClick}
            >
              {hasChildren && (
                <IconButton
                  icon={
                    isToggled ? ICON_BUTTON_ICONS.CHEVRON_DOWN : ICON_BUTTON_ICONS.CHEVRON_RIGHT
                  }
                  withFill={false}
                  withBorder={false}
                  wrapClassName="industry-code-item-toggle"
                />
              )}
              {!hasChildren && <div style={{ width: '18px', height: '18px', flexShrink: 0 }} />}
              <IconButton
                icon={
                  isSelected
                    ? ICON_BUTTON_ICONS.CHECK_SQUARE_FILL
                    : showInnerSelectionsDot
                      ? ICON_BUTTON_ICONS.CHECK_SQUARE
                      : ICON_BUTTON_ICONS.SQUARE
                }
                type={ICON_BUTTON_TYPES.BLUE}
                withFill={false}
                withBorder={false}
                wrapClassName={`industry-code-item-check ${!isSelected && showInnerSelectionsDot ? 'light-blue' : ''}`}
                onClick={handleCheckClick}
              />
              <div className="industry-code-item-name">
                {state.profilerIndustriesSearchIsTyping
                  ? code.name
                  : highlightMatches(code.name, state.profilerIndustriesSearchSearchKey)}
              </div>
            </div>
            {hasChildren &&
              isToggled &&
              code.children.map((child) => renderIndustryCode(child, level + 1))}
          </div>
        );
      },
      [state, highlightMatches, hasSelectedChildren]
    );

    if (utilsStore.windowWidth >= 834) {
      return null;
    }

    return (
      <div
        className="mobile-filters-container"
        style={
          searchType === UI_OPTIONS[UI_OPTION_KEYS.SEARCH_TYPE].Profiler &&
          state.profilerSearchCategoryFields[
            PROFILER_SEARCH_CATEGORY_FIELDS.SEARCH_CATEGORY_MOBILE.NAME
          ][0] === UI_OPTIONS[UI_OPTION_KEYS.PROFILER_SEARCH_CATEGORY]['Name']
            ? { paddingBottom: 0 }
            : {}
        }
      >
        <button onClick={() => navigate(-1)} className="backlink">
          Search results
        </button>

        <div className="fields">
          {searchType !== UI_OPTIONS[UI_OPTION_KEYS.SEARCH_TYPE].Profiler &&
            fundsInvestmentCriteriaFields[SEARCH_FIELDS.SEARCH_TYPE.ID]}
          {searchType === UI_OPTIONS[UI_OPTION_KEYS.SEARCH_TYPE].Profiler && (
            <>
              {profilerAccessStore.isProfilerDemo && (
                <div
                  className={`profiler-demo-mobile-filter-info-message${
                    profilerAccessStore.demoSearchExceededLimit ? ' exceeded' : ''
                  }`}
                >
                  <div className="title">
                    {profilerAccessStore.demoSearchExceededLimit ? (
                      <>
                        You&apos;ve used all {profilerAccessStore.demoSearchMaxCount} demo searches
                      </>
                    ) : (
                      <>
                        You have {profilerAccessStore.demoSearchRemainingCount} out of{' '}
                        {profilerAccessStore.demoSearchMaxCount} searches remaining
                      </>
                    )}
                  </div>
                  <div className="text">
                    {profilerAccessStore.demoSearchExceededLimit ? (
                      <>
                        Contact{' '}
                        <a href="mailto:support@transact.digital">support@transact.digital</a> for
                        an upgrade to continue.
                      </>
                    ) : (
                      <>Upgrade to full access to unlock unlimited searches.</>
                    )}
                  </div>
                </div>
              )}
              {fundsInvestmentCriteriaFields[SEARCH_FIELDS.SEARCH_TYPE.ID]}
              <MultiSelect
                field={PROFILER_SEARCH_CATEGORY_FIELDS.SEARCH_CATEGORY_MOBILE}
                value={
                  state.profilerSearchCategoryFields[
                    PROFILER_SEARCH_CATEGORY_FIELDS.SEARCH_CATEGORY_MOBILE.NAME
                  ]
                }
                setFieldValue={state.setProfilerSearchCategoryFieldValue}
                values={state.profilerCategories.filter(({ disabled }) => !disabled)}
                inputWrap={{ enable: true, className: 'col' }}
                // disabled={isLoading}
              />
              {state.profilerSearchCategoryFields[
                PROFILER_SEARCH_CATEGORY_FIELDS.SEARCH_CATEGORY_MOBILE.NAME
              ][0] === UI_OPTIONS[UI_OPTION_KEYS.PROFILER_SEARCH_CATEGORY]['Characteristics'] && (
                <>
                  {profilerCompanyCriteriaFields[PROFILER_SEARCH_FIELDS.INDUSTRY.ID]}
                  {profilerCompanyCriteriaFields[PROFILER_SEARCH_FIELDS.VALUE_CHAIN.ID]}
                  {profilerCompanyCriteriaFields[PROFILER_SEARCH_FIELDS.PRODUCT_PORTFOLIO.ID]}
                  {
                    profilerCompanyCriteriaFields[
                      PROFILER_SEARCH_FIELDS.REVENUE_MIN.ID + PROFILER_SEARCH_FIELDS.REVENUE_MAX.ID
                    ]
                  }
                  {isProfilerAdvancedCriteriaDisabled ? (
                    <>
                      {
                        profilerCompanyCriteriaFields[
                          PROFILER_SEARCH_CATEGORIES.ADVANCED_SEARCH_CRITERIA
                        ][
                          PROFILER_SEARCH_FIELDS.EMPLOYEES_MIN.ID +
                            PROFILER_SEARCH_FIELDS.EMPLOYEES_MAX.ID
                        ]
                      }
                      {
                        profilerCompanyCriteriaFields[
                          PROFILER_SEARCH_CATEGORIES.ADVANCED_SEARCH_CRITERIA
                        ][PROFILER_SEARCH_FIELDS.HEADQUARTER.ID]
                      }
                      {
                        profilerCompanyCriteriaFields[
                          PROFILER_SEARCH_CATEGORIES.ADVANCED_SEARCH_CRITERIA
                        ][PROFILER_SEARCH_FIELDS.CITY_ZIPCODE.ID]
                      }
                      {
                        profilerCompanyCriteriaFields[
                          PROFILER_SEARCH_CATEGORIES.ADVANCED_SEARCH_CRITERIA
                        ][PROFILER_SEARCH_FIELDS.DISTANCE.ID]
                      }
                    </>
                  ) : (
                    <>
                      {
                        profilerCompanyCriteriaFields[
                          PROFILER_SEARCH_CATEGORIES.ADVANCED_SEARCH_CRITERIA
                        ][PROFILER_SEARCH_FIELDS.CAGR_MIN.ID + PROFILER_SEARCH_FIELDS.CAGR_MAX.ID]
                      }
                      {
                        profilerCompanyCriteriaFields[
                          PROFILER_SEARCH_CATEGORIES.ADVANCED_SEARCH_CRITERIA
                        ][PROFILER_SEARCH_FIELDS.CAGR_PERIOD.ID]
                      }
                      {
                        profilerCompanyCriteriaFields[
                          PROFILER_SEARCH_CATEGORIES.ADVANCED_SEARCH_CRITERIA
                        ][
                          PROFILER_SEARCH_FIELDS.EBIT_MARGIN_MIN.ID +
                            PROFILER_SEARCH_FIELDS.EBIT_MARGIN_MAX.ID
                        ]
                      }
                      {
                        profilerCompanyCriteriaFields[
                          PROFILER_SEARCH_CATEGORIES.ADVANCED_SEARCH_CRITERIA
                        ][
                          PROFILER_SEARCH_FIELDS.EMPLOYEES_MIN.ID +
                            PROFILER_SEARCH_FIELDS.EMPLOYEES_MAX.ID
                        ]
                      }
                      {!isProfilerAdvancedCriteriaSituationDisabled && (
                        <>
                          {
                            profilerCompanyCriteriaFields[
                              PROFILER_SEARCH_CATEGORIES.ADVANCED_SEARCH_CRITERIA
                            ][PROFILER_SEARCH_FIELDS.SITUATION.ID]
                          }
                        </>
                      )}
                      {!isProfilerAdvancedCriteriaOwnershipDisabled && (
                        <>
                          {
                            profilerCompanyCriteriaFields[
                              PROFILER_SEARCH_CATEGORIES.ADVANCED_SEARCH_CRITERIA
                            ][PROFILER_SEARCH_FIELDS.LISTING_STATUS.ID]
                          }
                          {
                            profilerCompanyCriteriaFields[
                              PROFILER_SEARCH_CATEGORIES.ADVANCED_SEARCH_CRITERIA
                            ][PROFILER_SEARCH_FIELDS.OWNERSHIP_STRUCTURES.ID]
                          }
                          {
                            profilerCompanyCriteriaFields[
                              PROFILER_SEARCH_CATEGORIES.ADVANCED_SEARCH_CRITERIA
                            ][PROFILER_SEARCH_FIELDS.OWNERSHIP_BASIS.ID]
                          }
                        </>
                      )}
                      {
                        profilerCompanyCriteriaFields[
                          PROFILER_SEARCH_CATEGORIES.ADVANCED_SEARCH_CRITERIA
                        ][PROFILER_SEARCH_FIELDS.HEADQUARTER.ID]
                      }
                      {
                        profilerCompanyCriteriaFields[
                          PROFILER_SEARCH_CATEGORIES.ADVANCED_SEARCH_CRITERIA
                        ][PROFILER_SEARCH_FIELDS.CITY_ZIPCODE.ID]
                      }
                      {
                        profilerCompanyCriteriaFields[
                          PROFILER_SEARCH_CATEGORIES.ADVANCED_SEARCH_CRITERIA
                        ][PROFILER_SEARCH_FIELDS.DISTANCE.ID]
                      }
                    </>
                  )}
                </>
              )}
              {state.profilerSearchCategoryFields[
                PROFILER_SEARCH_CATEGORY_FIELDS.SEARCH_CATEGORY_MOBILE.NAME
              ][0] === UI_OPTIONS[UI_OPTION_KEYS.PROFILER_SEARCH_CATEGORY]['Industries'] && (
                <>
                  <div className="search-key-input-wrapper-industry-codes">
                    <div className="search-key-label-industry-codes">
                      Industries<font className="red-asterisk">*</font>
                    </div>
                    <div className="search-key-input-wrap-industry-codes">
                      <input
                        className={`${selectedIndustryCodes.length ? 'placeholder-black' : ''} ${state.profilerIndustriesSearchTreeToggled ? 'force-focused' : ''}`}
                        type="text"
                        value={state.profilerIndustriesSearchSearchKey}
                        onChange={(e) => state.setprofilerIndustriesSearchSearchKey(e.target.value)}
                        placeholder={
                          selectedIndustryCodes.length === 1
                            ? selectedIndustryCodes[0].name
                            : selectedIndustryCodes.length > 1
                              ? `${selectedIndustryCodes.length} selected`
                              : 'Select'
                        }
                        disabled={
                          // state.isLoading ||
                          // state.isInitialLoading ||
                          state.isProfilerIndustryCodesSearchFieldsDisabled
                        }
                        onFocus={() => {
                          if (!state.profilerIndustriesSearchTreeToggled) {
                            state.toggleProfilerIndustriesSearchTree();
                          }
                        }}
                      />
                      <IconButton
                        id="profiler-industry-codes-drop-icon"
                        icon={
                          state.profilerIndustriesSearchTreeToggled
                            ? ICON_BUTTON_ICONS.CHEVRON_UP
                            : ICON_BUTTON_ICONS.CHEVRON_DOWN
                        }
                        withFill={false}
                        withBorder={false}
                        onClick={state.toggleProfilerIndustriesSearchTree}
                        disabled={
                          // state.isLoading ||
                          // state.isInitialLoading ||
                          state.isProfilerIndustryCodesSearchFieldsDisabled
                        }
                      />
                      {state.profilerIndustriesSearchTreeToggled && (
                        <div className="industry-codes-wrapper-outer in-search-results-page">
                          <div className="industry-codes-actions-wrapper">
                            <IconButton
                              type={ICON_BUTTON_TYPES.BLUE}
                              withFill={false}
                              withBorder={false}
                              innerText="Toggle All"
                              onClick={state.toggleAllIndustryCodes}
                            />
                            <IconButton
                              type={ICON_BUTTON_TYPES.BLUE}
                              withFill={false}
                              withBorder={false}
                              innerText="Untoggle All"
                              onClick={state.untoggleAllIndustryCodes}
                            />
                            <IconButton
                              type={ICON_BUTTON_TYPES.BLUE}
                              withFill={false}
                              withBorder={false}
                              innerText="Clear All"
                              onClick={state.clearAllIndustryCodes}
                            />
                          </div>
                          <div className="industry-codes-wrapper">
                            {state.industryCodesRaw.map((code) => renderIndustryCode(code, 1))}
                          </div>
                          <div className="fade-top" />
                          <div className="fade-bottom" />
                        </div>
                      )}
                    </div>
                  </div>
                  {
                    profilerIndustryCodesFields[
                      PROFILER_INDUSTRY_CODES_SEARCH_FIELDS.REVENUE_MIN.ID +
                        PROFILER_INDUSTRY_CODES_SEARCH_FIELDS.REVENUE_MAX.ID
                    ]
                  }
                  {isProfilerAdvancedCriteriaDisabled ? (
                    <>
                      {
                        profilerIndustryCodesFields[
                          PROFILER_INDUSTRY_CODES_SEARCH_CATEGORIES.ADVANCED_SEARCH_CRITERIA
                        ][
                          PROFILER_INDUSTRY_CODES_SEARCH_FIELDS.EMPLOYEES_MIN.ID +
                            PROFILER_INDUSTRY_CODES_SEARCH_FIELDS.EMPLOYEES_MAX.ID
                        ]
                      }
                      {
                        profilerIndustryCodesFields[
                          PROFILER_INDUSTRY_CODES_SEARCH_CATEGORIES.ADVANCED_SEARCH_CRITERIA
                        ][PROFILER_INDUSTRY_CODES_SEARCH_FIELDS.SITUATION.ID]
                      }
                      {
                        profilerIndustryCodesFields[
                          PROFILER_INDUSTRY_CODES_SEARCH_CATEGORIES.ADVANCED_SEARCH_CRITERIA
                        ][PROFILER_INDUSTRY_CODES_SEARCH_FIELDS.HEADQUARTER.ID]
                      }
                      {
                        profilerIndustryCodesFields[
                          PROFILER_INDUSTRY_CODES_SEARCH_CATEGORIES.ADVANCED_SEARCH_CRITERIA
                        ][PROFILER_INDUSTRY_CODES_SEARCH_FIELDS.CITY_ZIPCODE.ID]
                      }
                      {
                        profilerIndustryCodesFields[
                          PROFILER_INDUSTRY_CODES_SEARCH_CATEGORIES.ADVANCED_SEARCH_CRITERIA
                        ][PROFILER_INDUSTRY_CODES_SEARCH_FIELDS.DISTANCE.ID]
                      }
                    </>
                  ) : (
                    <>
                      {
                        profilerIndustryCodesFields[
                          PROFILER_INDUSTRY_CODES_SEARCH_CATEGORIES.ADVANCED_SEARCH_CRITERIA
                        ][
                          PROFILER_INDUSTRY_CODES_SEARCH_FIELDS.CAGR_MIN.ID +
                            PROFILER_INDUSTRY_CODES_SEARCH_FIELDS.CAGR_MAX.ID
                        ]
                      }
                      {
                        profilerIndustryCodesFields[
                          PROFILER_INDUSTRY_CODES_SEARCH_CATEGORIES.ADVANCED_SEARCH_CRITERIA
                        ][PROFILER_INDUSTRY_CODES_SEARCH_FIELDS.CAGR_PERIOD.ID]
                      }
                      {
                        profilerIndustryCodesFields[
                          PROFILER_INDUSTRY_CODES_SEARCH_CATEGORIES.ADVANCED_SEARCH_CRITERIA
                        ][
                          PROFILER_INDUSTRY_CODES_SEARCH_FIELDS.EBIT_MARGIN_MIN.ID +
                            PROFILER_INDUSTRY_CODES_SEARCH_FIELDS.EBIT_MARGIN_MAX.ID
                        ]
                      }
                      {
                        profilerIndustryCodesFields[
                          PROFILER_INDUSTRY_CODES_SEARCH_CATEGORIES.ADVANCED_SEARCH_CRITERIA
                        ][
                          PROFILER_INDUSTRY_CODES_SEARCH_FIELDS.EMPLOYEES_MIN.ID +
                            PROFILER_INDUSTRY_CODES_SEARCH_FIELDS.EMPLOYEES_MAX.ID
                        ]
                      }
                      {!isProfilerAdvancedCriteriaSituationDisabled && (
                        <>
                          {
                            profilerIndustryCodesFields[
                              PROFILER_INDUSTRY_CODES_SEARCH_CATEGORIES.ADVANCED_SEARCH_CRITERIA
                            ][PROFILER_INDUSTRY_CODES_SEARCH_FIELDS.SITUATION.ID]
                          }
                        </>
                      )}
                      {!isProfilerAdvancedCriteriaOwnershipDisabled && (
                        <>
                          {
                            profilerIndustryCodesFields[
                              PROFILER_INDUSTRY_CODES_SEARCH_CATEGORIES.ADVANCED_SEARCH_CRITERIA
                            ][PROFILER_INDUSTRY_CODES_SEARCH_FIELDS.LISTING_STATUS.ID]
                          }
                          {
                            profilerIndustryCodesFields[
                              PROFILER_INDUSTRY_CODES_SEARCH_CATEGORIES.ADVANCED_SEARCH_CRITERIA
                            ][PROFILER_INDUSTRY_CODES_SEARCH_FIELDS.OWNERSHIP_STRUCTURES.ID]
                          }
                          {
                            profilerIndustryCodesFields[
                              PROFILER_INDUSTRY_CODES_SEARCH_CATEGORIES.ADVANCED_SEARCH_CRITERIA
                            ][PROFILER_INDUSTRY_CODES_SEARCH_FIELDS.OWNERSHIP_BASIS.ID]
                          }
                        </>
                      )}
                      {
                        profilerIndustryCodesFields[
                          PROFILER_INDUSTRY_CODES_SEARCH_CATEGORIES.ADVANCED_SEARCH_CRITERIA
                        ][PROFILER_INDUSTRY_CODES_SEARCH_FIELDS.HEADQUARTER.ID]
                      }
                      {
                        profilerIndustryCodesFields[
                          PROFILER_INDUSTRY_CODES_SEARCH_CATEGORIES.ADVANCED_SEARCH_CRITERIA
                        ][PROFILER_INDUSTRY_CODES_SEARCH_FIELDS.CITY_ZIPCODE.ID]
                      }
                      {
                        profilerIndustryCodesFields[
                          PROFILER_INDUSTRY_CODES_SEARCH_CATEGORIES.ADVANCED_SEARCH_CRITERIA
                        ][PROFILER_INDUSTRY_CODES_SEARCH_FIELDS.DISTANCE.ID]
                      }
                    </>
                  )}
                </>
              )}
              {state.profilerSearchCategoryFields[
                PROFILER_SEARCH_CATEGORY_FIELDS.SEARCH_CATEGORY_MOBILE.NAME
              ][0] === UI_OPTIONS[UI_OPTION_KEYS.PROFILER_SEARCH_CATEGORY]['Name'] && (
                <ProfilerCompanyNameSearch
                  isTyping={state.profilerNameSearchIsTyping}
                  searchKey={state.profilerNameSearchSearchKey}
                  setSearchKey={state.setprofilerNameSearchSearchKey}
                  clearSearchKey={state.profilerNameSearchClearSearchKey}
                  searchKeyResults={state.profilerNameSearchSearchKeyResults}
                  isLoading={state.profilerNameSearchIsLoading}
                  loadError={state.profilerNameSearchLoadError}
                  isLoadingCompanyData={state.profilerNameSearchIsLoadingCompanyData}
                  loadCompanyData={state.profilerNameSearchLoadCompanyData}
                />
              )}
              {state.profilerSearchCategoryFields[
                PROFILER_SEARCH_CATEGORY_FIELDS.SEARCH_CATEGORY_MOBILE.NAME
              ][0] === UI_OPTIONS[UI_OPTION_KEYS.PROFILER_SEARCH_CATEGORY]['Bulk'] && (
                <BulkAdjustPanel bulkAdjust={bulkAdjust} />
              )}
            </>
          )}
          {searchType === UI_OPTIONS[UI_OPTION_KEYS.SEARCH_TYPE].Funds && (
            <>
              <MultiSelect
                field={FUND_SEARCH_CATEGORY_FIELDS.SEARCH_CATEGORY_MOBILE}
                value={
                  state.fundsSearchCategoryFields[
                    FUND_SEARCH_CATEGORY_FIELDS.SEARCH_CATEGORY_MOBILE.NAME
                  ]
                }
                setFieldValue={state.setFundsSearchCategoryFieldValue}
                values={state.fundsCategories.filter(({ disabled }) => !disabled)}
                inputWrap={{ enable: true, className: 'col' }}
                disabled={isLoading}
              />
              {state.fundsSearchCategoryFields[
                FUND_SEARCH_CATEGORY_FIELDS.SEARCH_CATEGORY_MOBILE.NAME
              ][0] === UI_OPTIONS[UI_OPTION_KEYS.FUND_SEARCH_CATEGORY]['Investment criteria'] && (
                <>
                  {fundsInvestmentCriteriafieldsSections.map((field, idx) => (
                    <Fragment key={idx}>{field}</Fragment>
                  ))}
                </>
              )}
            </>
          )}
          {searchType === UI_OPTIONS[UI_OPTION_KEYS.SEARCH_TYPE]['Portfolio Co.'] && (
            <>
              <MultiSelect
                field={PORTFOLIO_SEARCH_CATEGORY_FIELDS.SEARCH_CATEGORY_MOBILE}
                value={
                  state.portfoliosSearchCategoryFields[
                    PORTFOLIO_SEARCH_CATEGORY_FIELDS.SEARCH_CATEGORY_MOBILE.NAME
                  ]
                }
                setFieldValue={state.setPortfoliosSearchCategoryFieldValue}
                values={state.portfoliosCategories.filter(({ disabled }) => !disabled)}
                inputWrap={{ enable: true, className: 'col' }}
                disabled={isLoading}
              />
              {state.portfoliosSearchCategoryFields[
                PORTFOLIO_SEARCH_CATEGORY_FIELDS.SEARCH_CATEGORY_MOBILE.NAME
              ][0] ===
                UI_OPTIONS[UI_OPTION_KEYS.PORTFOLIO_SEARCH_CATEGORY]['Investment criteria'] && (
                <>
                  {
                    portfoliosInvestmentCriteriaFields[
                      SEARCH_FIELDS_PORTFOLIO_INVESTMENT_CRITERIA.INDUSTRY.ID
                    ]
                  }
                  {
                    portfoliosInvestmentCriteriaFields[
                      SEARCH_FIELDS_PORTFOLIO_INVESTMENT_CRITERIA.HEADQUARTER.ID
                    ]
                  }
                  {
                    portfoliosInvestmentCriteriaFields[
                      SEARCH_FIELDS_PORTFOLIO_INVESTMENT_CRITERIA.GEOGRAPHICAL_FOCUS.ID
                    ]
                  }
                  {
                    portfoliosInvestmentCriteriaFields[
                      SEARCH_FIELDS_PORTFOLIO_INVESTMENT_CRITERIA.REVENUE.ID
                    ]
                  }
                </>
              )}
            </>
          )}
          {searchType === UI_OPTIONS[UI_OPTION_KEYS.SEARCH_TYPE].Individuals && (
            <IndividualsNameSearch inputId="search-results-individuals-searchKey-mobile" />
          )}
        </div>

        {searchType === UI_OPTIONS[UI_OPTION_KEYS.SEARCH_TYPE].Profiler && (
          <>
            {state.profilerSearchCategoryFields[
              PROFILER_SEARCH_CATEGORY_FIELDS.SEARCH_CATEGORY_MOBILE.NAME
            ][0] === UI_OPTIONS[UI_OPTION_KEYS.PROFILER_SEARCH_CATEGORY]['Characteristics'] && (
              <div className="form-footer">
                <button
                  className="btn btn-primary disable-fade-primary"
                  onClick={state.onSearch}
                  disabled={state.isProfilerCompanyCriteriaSearchDisabled}
                >
                  Apply criteria
                </button>
              </div>
            )}
            {state.profilerSearchCategoryFields[
              PROFILER_SEARCH_CATEGORY_FIELDS.SEARCH_CATEGORY_MOBILE.NAME
            ][0] === UI_OPTIONS[UI_OPTION_KEYS.PROFILER_SEARCH_CATEGORY]['Industries'] && (
              <div className="form-footer">
                <button
                  className="btn btn-primary disable-fade-primary"
                  onClick={state.onSearch}
                  disabled={state.isProfilerIndustryCodesSearchDisabled}
                >
                  Apply criteria
                </button>
              </div>
            )}
          </>
        )}
        {searchType === UI_OPTIONS[UI_OPTION_KEYS.SEARCH_TYPE].Funds && (
          <>
            {state.fundsSearchCategoryFields[
              FUND_SEARCH_CATEGORY_FIELDS.SEARCH_CATEGORY_MOBILE.NAME
            ][0] === UI_OPTIONS[UI_OPTION_KEYS.FUND_SEARCH_CATEGORY]['Investment criteria'] && (
              <div className="form-footer">
                <button
                  className="btn btn-primary disable-fade-primary"
                  onClick={state.onSearch}
                  disabled={state.isFundsInvestmentCriteriaSearchDisabled}
                >
                  Apply criteria
                </button>
              </div>
            )}
          </>
        )}
        {searchType === UI_OPTIONS[UI_OPTION_KEYS.SEARCH_TYPE]['Portfolio Co.'] && (
          <>
            {state.portfoliosSearchCategoryFields[
              PORTFOLIO_SEARCH_CATEGORY_FIELDS.SEARCH_CATEGORY_MOBILE.NAME
            ][0] ===
              UI_OPTIONS[UI_OPTION_KEYS.PORTFOLIO_SEARCH_CATEGORY]['Investment criteria'] && (
              <div className="form-footer">
                <button
                  className="btn btn-primary disable-fade-primary"
                  onClick={state.onSearch}
                  disabled={state.isPortfoliosInvestmentCriteriaSearchDisabled}
                >
                  Apply criteria
                </button>
              </div>
            )}
          </>
        )}
      </div>
    );
  }
);

export default MobileFilters;
